import React, { useEffect, useState } from 'react';
import { Form, Button, Card, Input, Select, message, Steps, Row, Col, Checkbox, Typography } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import ApiService from '../Components/apiService';
import { BASE_URL } from '../Components/Constant';
import ApprovalSteps from './ApprovalStep';
import BasicInformation from './ApprovalBasicInfo';
import EntryCriteria from './ApprovalEntryCriteria';
import ConsolidationSettings from './ApprovalConsolidationSettings';
// import CustomNotificationTemplate from './ApprovalCustomNotificationTemplate';
import SelectBackupAdminUser from './ApprovalBackupAdmin';
import CommentSettings from './ApprovalCommentSettings';
import InitialSubmissionAction from './ApprovalInitialSubmission';
import FinalActions from './ApprovalFinalAction';
import { useNavigate } from 'react-router-dom';
import TriggeringApprovalCriteria from './TriggeringApprovalCriteria';

const { Option } = Select;
const { Step } = Steps;
const { Title } = Typography;

const CreateApprovalProcessEditor = ({ visible, onClose, object, process, fetchApprovalProcess }) => {
  const [form] = Form.useForm();
  const [objectList, setObjectList] = useState([]);
  const [availableFields, setAvailableFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showFilterLayout, setShowFilterLayout] = useState(false);
  const [selectedObject, setSelectedObject] = useState(null);
  const [users, setUsers] = useState([]);
  const [stepValues, setStepValues] = useState([]);
  const [approvalStepsParent, setApprovalStepsParent] = useState([]);
  const [manualTriggering, setManualTriggering] = useState(true);
  const [triggeringFilterCriteria, setTriggeringFilterCriteria] = useState([{ 1: { field: '', value: '', displayValue: '' } }]);  
  const [entryCriteria, setEntryCriteria] = useState([{ 1: { field: '', value: '', displayValue: '' } }]);
  const navigate = useNavigate();
  const [logicForTriggering, setLogicForTriggering] = useState('1');
  const [logicForEntryCriteria, setLogicForEntryCriteria] = useState('1');
  const [valueOperatorEntry, setValueOperatorEntry] = useState('');
  const [valueOperatorTriggering, setValueOperatorTriggering] = useState('');

  const handleApprovalStepsChange = (steps) => {
    setApprovalStepsParent(steps); // Update parent's state
  };

  useEffect(() => {
    const fetchObjects = async () => {
      const apiServiceForObjects = new ApiService(
        `${BASE_URL}/mt_objects`,
        { 'Content-Type': 'application/json' },
        'GET'
      );

      try {
        setLoading(true);
        const response = await apiServiceForObjects.makeCall();
        setObjectList(response);
      } catch (error) {
        message.error('Failed to fetch objects.');
      } finally {
        setLoading(false);
      }
    };

    fetchObjects();
  }, []);



  useEffect(() => {
    if (!selectedObject) return;

    const fetchFields = async () => {
      const apiServiceForFields = new ApiService(
        `${BASE_URL}/mt_fields/object/${selectedObject}`,
        { 'Content-Type': 'application/json' },
        'GET'
      );

      try {
        setLoading(true);
        const response = await apiServiceForFields.makeCall();
        setAvailableFields(response);
      } catch (error) {
        message.error('Failed to fetch fields.');
      } finally {
        setLoading(false);
      }
    };

    fetchFields();
  }, [selectedObject]);

  useEffect(() => {
    const fetchUsers = async () => {
      const apiServiceForLookup = new ApiService(
        `${BASE_URL}/fetch_records/User`,
        { 'Content-Type': 'application/json' },
        'GET'
      );

      try {
        setLoading(true);
        const response = await apiServiceForLookup.makeCall();
        const formattedUsers = response?.records?.map((user) => ({
          id: user.id || user._id,
          name: user.Name || user.username || 'No Name Available',
        }));
        setUsers(formattedUsers);
      } catch (error) {
        message.error('Failed to fetch users.');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  
  useEffect(() => {
    if (!process) {
      form.resetFields();
      return;
    }
  
    const isManualTriggering = process.manual_triggering_criteria;
  
    setSelectedObject(process.object_name);
    setApprovalStepsParent(process.steps);
  
    // --- Entry Criteria ---
    const existingEntryCriteriaFilters =
      process.entry_criteria?.filters && typeof process.entry_criteria.filters === 'object'
        ? Object.entries(process.entry_criteria.filters).map(([key, { field, value }]) => {
            let operator = '';
            let actualValue = value;
  
            if (typeof value === 'object' && value !== null) {
              operator = Object.keys(value)[0];
              actualValue = value[operator];
            }
  
            if (operator) setValueOperatorEntry(operator);
  
            return {
              field,
              value,
              displayValue: actualValue,
              operator,
            };
          })
        : [{ field: '', value: '', displayValue: '', operator: '' }];
  
    setEntryCriteria(existingEntryCriteriaFilters);
  
    // --- Triggering Criteria ---
    const existingTriggeringFilters =
      process.triggering_criteria?.filters && typeof process.triggering_criteria.filters === 'object'
        ? Object.entries(process.triggering_criteria.filters).map(([key, { field, value }]) => {
            let operator = '';
            let actualValue = value;
  
            if (typeof value === 'object' && value !== null) {
              operator = Object.keys(value)[0];
              actualValue = value[operator];
            }
  
            if (operator) setValueOperatorTriggering(operator);
  
            return {
              field,
              value,
              displayValue: actualValue,
              operator,
            };
          })
        : [{ field: '', value: '', displayValue: '', operator: '' }];
  
    setTriggeringFilterCriteria(existingTriggeringFilters);
    setManualTriggering(isManualTriggering);
  
    // --- Set Form Values ---
    form.setFieldsValue({
      name: process.Name,
      objectName: process.object_name,
      active: process.active,
      manual: isManualTriggering,
      consolidateApprovals: process.consolidate_aprovals,
      consolidateNotification: process.consolidate_notifications,
      continuePendingApprovals: process.consolidate_pending_approvals_on_reject,
      // backupAdminUser: process.backUpAdminUser_id,
      backupAdmin: {
        user: process.backup_admin?.user_id,
        send_email: process.backup_admin?.send_email || false,
        send_notification: process.backup_admin?.send_notification || false,
      },      
      submissionCommentsEnabled: process.submission_comments_enabled,
      approvalCommentsMandatory: process.approval_comments_mandatroy,
      rejectionCommentsMandatory: process.rejection_comments_mandatroy,
      recallCommentsEnabled: process.recall_comments_enabled,
      filterCriteria: process.initial_submission_action?.map(({ action, field, value }) => ({
        action, field, value,
      })),
      approvalActions: process.final_submission_actions?.map(({ action, field, value }) => ({
        action, field, value,
      })),
      rejectionActions: process.final_rejection_actions?.map(({ action, field, value }) => ({
        action, field, value,
      })),
      triggering_criteria: !isManualTriggering
        ? {
            filters: process.triggering_criteria?.filters || {},
            logic: process.triggering_criteria?.logic || '1',
          }
        : {},
      entry_criteria: {
        filters: process.entry_criteria?.filters || {},
        logic: process.entry_criteria?.logic || '1',
      },
      manual_triggering_criteria: isManualTriggering,
      steps: process.steps,
    });
  }, [process, form]);
  
  

  // const handleNext = () => {
  //   form
  //     .validateFields()
  //     .then((values) => {
  //       console.log('valuess:::',values);
  //       // Combine previous step values and current step values using the spread operator
  //       setStepValues((prevStepValues) => {
  //         const updatedValues = [...prevStepValues, values];
  //         return updatedValues;
  //       });
  //       setCurrentStep(currentStep + 1); // Go to next step
  //     })
  //     .catch((info) => {
  //       console.log('Validate Failed:', info);
  //     });
  // };

  const handleNext = () => {
    form.validateFields().then((values) => {
      const allValues = { ...values, entryCriteria,triggeringCriteria: triggeringFilterCriteria};
      console.log('Final values:', allValues);
      setStepValues(prev => [...prev, allValues]);
      setCurrentStep(currentStep + 1);
    });
  };
    
  const handleNextStep = (targetStep) => {
    form
      .validateFields()
      .then((values) => {
        form.validateFields(); // Force re-validation
        setStepValues((prevStepValues) => {
          const updatedValues = [...prevStepValues];
          updatedValues[currentStep] = values; // Update current step values
          return updatedValues;
        });
        console.log('stepvaluesss:',stepValues);

  
        // Navigate to the next or specific step
        setCurrentStep(targetStep !== undefined ? targetStep : currentStep + 1);
      })
      .catch((info) => {
        console.log('Validate Failed:', info);
      });
  };


  const normalizeFilters = (filtersArray) =>
    (filtersArray || []).reduce((acc, filter, index) => {
      if (filter.field) {
        acc[index + 1] = {
          field: filter.field,
          value:
            filter.operator && filter.value === ""
              ? { [filter.operator]: "" }
              : filter.value,
        };
      }

      return acc;
    }, {});
  
  const onFinish = async (formValues) => {
  
    console.log('formValues:',formValues);
    console.log('stepValues:',stepValues);
    // Combine all step values (including the current one) into one object using the spread operator
    const allStepValues = stepValues.reduce((acc, step, index) => {
      return { ...acc, [`step_${index + 1}`]: step };
    }, {});
  
    // Now, you have all the step values combined
    const finalFormValues = {
      ...allStepValues,
      ...formValues, // Add the final form values (if any)
    };
  
  

    console.log('final form values ', finalFormValues);
    // const filterObjTriggering = normalizeFilters(finalFormValues.step_2?.triggeringCriteria);
    // const filterObjEntryCriteria = normalizeFilters(finalFormValues.step_3?.entryCriteria);
    const filterObjTriggering =
      finalFormValues.step_2 ? 
      normalizeFilters(finalFormValues.step_2?.triggeringCriteria) :
      process?.triggering_criteria?.filters;

    const filterObjEntryCriteria =
      finalFormValues.step_3 ? 
      normalizeFilters(finalFormValues.step_3?.entryCriteria) :
      process?.entry_criteria?.filters;


    const body = {
      approval_process: {
        Name: finalFormValues?.step_1?.name,
        object_name: finalFormValues?.step_1?.objectName,
        triggering_criteria: !manualTriggering
        ? {
            filters: filterObjTriggering ,
            logic: logicForTriggering || '1',
            // valueOperator: triggeringFilterCriteria?.valueOperator || '',
          }
        : {},

        entry_criteria: {
          filters: filterObjEntryCriteria ,
          logic: logicForEntryCriteria || '1',
          // valueOperator: entryCriteria?.valueOperator || '',
        },
  
        steps: approvalStepsParent || [],
        active: finalFormValues.step_1?.active,
        manual_triggering_criteria: manualTriggering,
        consolidate_aprovals: finalFormValues.step_4?.consolidateApprovals,
        consolidate_notifications: finalFormValues.step_4?.consolidateNotification,
        consolidate_pending_approvals_on_reject: finalFormValues.step_4?.continuePendingApprovals,
        submission_comments_enabled: finalFormValues.step_6?.submissionCommentsEnabled,
        approval_comments_mandatroy: finalFormValues.step_6?.approvalCommentsMandatory,
        rejection_comments_mandatroy: finalFormValues.step_6?.rejectionCommentsMandatory,
        recall_comments_enabled: finalFormValues.step_6?.recallCommentsEnabled, 
        backup_admin: {
          user_id: finalFormValues.step_5?.backupAdmin?.user,
          send_email: finalFormValues.step_5?.backupAdmin?.send_email || false,
          send_notification: finalFormValues.step_5?.backupAdmin?.send_notification || false,
        },          
        // backUpAdminUser: finalFormValues.step_5?.backupAdminUser,     
        initial_submission_action: finalFormValues.step_7?.filterCriteria?.map((criteria) => ({
          action: criteria.action,
          field: criteria.field,
          value: criteria.value,
        })) || [],
        final_submission_actions: finalFormValues.approvalActions?.map((approval) => ({
          action: approval.action,
          field: approval.field,
          value: approval.value,
        })) || [],
        final_rejection_actions: finalFormValues.rejectionActions?.map((rejection) => ({
          action: rejection.action,
          field: rejection.field,
          value: rejection.value,
        })) || [],
      },
    };

    console.log('final bodyyy values ', body);

    try {
      const method = process ? 'PUT' : 'POST';
      const url = process
        ? `${BASE_URL}/approval_processes/${process._id}`
        : `${BASE_URL}/approval_processes`;
  
      const apiService = new ApiService(
        url,
        { 'Content-Type': 'application/json' },
        method,
        body
      );
  
      const response = await apiService.makeCall();
  
      message.success(`Approval process ${process ? 'updated' : 'created'} successfully!`);
      onClose(); // Close the drawer
      fetchApprovalProcess(); // Refresh data
    } catch (error) {
      console.log('error',JSON.stringify(error));
      
      console.error(`Error ${process ? 'updating' : 'creating'} approval process:`, error);

      
      const errorMessage = Object.entries(error)
            .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
            .join(' | ')
            .replace(/^errors:\s*/, '');
      message.error(errorMessage);
    }
  
  };

  const steps = [
    {
      title: 'Basic Information',
      content: <BasicInformation objectList={objectList} setSelectedObject={setSelectedObject} />,
    },
    {
      title: "Triggering Criteria",
      content:<TriggeringApprovalCriteria 
      availableFields={availableFields} 
      setManualTriggering={setManualTriggering} 
      setTriggeringFilterCriteria={setTriggeringFilterCriteria} 
      manualTriggering={manualTriggering}
      triggeringFilterCriteria={triggeringFilterCriteria}
      setLogicForTriggering={setLogicForTriggering}
      logicForTriggering={logicForTriggering}
      setValueOperatorTriggering={setValueOperatorTriggering}
      valueOperatorTriggering={valueOperatorTriggering}
      />
  
    },
    {
      title: 'Entry Criteria',
      content: (
        <EntryCriteria
          availableFields={availableFields}
          setEntryCriteria={setEntryCriteria}
          entryCriteria={entryCriteria}
          setLogicForEntryCriteria={setLogicForEntryCriteria}
          logicForEntryCriteria={logicForEntryCriteria}
          setValueOperatorEntry={setValueOperatorEntry}
          valueOperatorEntry={valueOperatorEntry}
          // setShowFilterLayout={setShowFilterLayout}
        />
      ),
    },
    {
      title: 'Consolidation Settings',
      content: <ConsolidationSettings form={form} />,
    },
    // { title: 'Custom Notification Template', content: <CustomNotificationTemplate /> },
    { title: 'Select Backup Admin User', content: <SelectBackupAdminUser users={users} fields={availableFields} /> },
    { title: 'Comment Settings', content: <CommentSettings form={form} /> },
    {
      title: 'Initial Submission Action',
      content: <InitialSubmissionAction availableFields={availableFields} selectedObject={selectedObject} />,
    },  
    {
      title: 'Approval Steps',
      content: (
        <ApprovalSteps
        selectedObject={selectedObject}
        availableFields={availableFields}
        users={users}
        onApprovalStepsChange={handleApprovalStepsChange}
        savedApprovalStep={approvalStepsParent}
        process={process}
        />
      ),
    },
    {
      title: 'Final Actions',
      content: <FinalActions availableFields={availableFields} />,
    },
  ];

  return (
    <Card>
      <Title level={3} style={{ marginBottom: 30 }}>
      {process ? 'Edit Approval Process' : 'New Approval Process'}
          </Title>
    <Row gutter={24} style={{ height: '100%' }}>
      <Col span={6}>
      <Steps
  current={currentStep}
  onChange={(step) => {
    if (process) {
      // Validate and store all steps up to the clicked step
      if (step > currentStep) {
        for (let i = currentStep; i < step; i++) {
          handleNextStep(i + 1);
        }
      } else {
        setCurrentStep(step); // Allow backward navigation without validation
      }
    }
  }}
  direction="vertical"
  style={{ marginRight: 20 }}
>
  {steps.map((item, index) => (
    <Step
      key={item.title}
      title={item.title}
      style={{ cursor: process ? 'pointer' : 'default' }}
      status={process ? 'finish' : index <= currentStep ? 'process' : 'wait'}
    />
  ))}
</Steps>


      </Col>
  
      <Col span={18}>
        <Card style={{  position: 'relative' }}>
          <Form form={form} onFinish={onFinish} style={{ height: '100%' }} initialValues={{ manual: true }}>
          <Title level={4} style={{ marginBottom: 30 }}>
            {steps[currentStep].title}
          </Title>
            <div style={{ paddingBottom: '30px' }}>{steps[currentStep].content}</div>
  
            {/* Button Section */}
            <div
              style={{
                position: 'absolute',
                bottom: '20px',
                right: '20px',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px', // Adds spacing between buttons
              }}
            >
              <Button danger onClick={onClose}>
                Cancel
              </Button>
              {currentStep > 0 && (
                <Button onClick={() => setCurrentStep(currentStep - 1)}>Previous</Button>
              )}
              {currentStep < steps.length - 1 && (
                <Button type="primary" onClick={handleNext}>
                Next
              </Button>
              
              )}
              {currentStep === steps.length - 1 && (
                <Button type="primary" htmlType="submit" >
                  Submit
                </Button>
              )}
            </div>
          </Form>
        </Card>
      </Col>
    </Row>
  </Card>
  
  );
};

export default CreateApprovalProcessEditor;
